import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import DataTable, { Column } from '@/components/DataTable';
import { PodusCheckerNavigation } from '@/components/Navbar';
import { useGetMyEarningsQuery, useGetMyEarningsSummaryQuery } from '@/store/api/apiSlice';
import { Earning, EarningStatus } from '@/types';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';

type FilterType = 'All' | 'Pending' | 'Paid';

export default function Earnings() {
  const currentUser = useAppSelector(selectCurrentUser);
  const [filter, setFilter] = useState<FilterType>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Fetch earnings data
  const statusFilter = filter === 'All' ? undefined : filter === 'Paid' ? EarningStatus.PAID : EarningStatus.PENDING;
  const { data: earnings = [], isLoading, error } = useGetMyEarningsQuery(statusFilter);
  const { data: summary } = useGetMyEarningsSummaryQuery();

  // Get user initials
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  // Filter and search
  const filteredData = useMemo(() => {
    return earnings.filter((item) => {
      const employeeName = `${item.investigation?.employeeFirstName || ''} ${item.investigation?.employeeLastName || ''}`.toLowerCase();
      const matchesSearch =
        item.investigationId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employeeName.includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [earnings, searchQuery]);

  const handleRowSelect = (index: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Format currency
  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '\u20A60';
    }
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleDownloadReceipt = async (earningId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://checker-api.podus.org/api'}/earnings/${earningId}/receipt`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download receipt');
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `earning-receipt-${earningId}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  const columns: Column<Earning>[] = [
    {
      key: 'sn',
      label: 'S/N',
      sortable: true,
      render: (_item, index) => (
        <span className="text-sm text-gray-700">
          {(currentPage - 1) * rowsPerPage + (index || 0) + 1}
        </span>
      ),
    },
    {
      key: 'investigationId',
      label: 'Transaction ID',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-900">{item.investigationId.slice(0, 12).toUpperCase()}</span>
      ),
    },
    {
      key: 'employeeName',
      label: 'Employee Name',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-900">
          {(item as any).employeeName || `${item.investigation?.employeeFirstName || ''} ${item.investigation?.employeeLastName || ''}`.trim() || 'N/A'}
        </span>
      ),
    },
    {
      key: 'checkType',
      label: 'Check Type',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-600 border-indigo-100 px-4 py-1 rounded-lg font-medium capitalize"
        >
          {(item as any).checkType || item.investigation?.checkType || 'N/A'}
        </Badge>
      ),
    },
    {
      key: 'dateSubmitted',
      label: 'Date Submitted',
      sortable: true,
      hasMenu: true,
      render: (item) => {
        const dateSubmitted = (item as any).dateSubmitted || item.createdAt;
        return (
          <span className="text-sm text-gray-700">
            {new Date(dateSubmitted).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        );
      },
    },
    {
      key: 'datePaid',
      label: 'Date Paid',
      sortable: true,
      hasMenu: true,
      render: (item) => {
        const datePaid = (item as any).datePaid || item.paidAt;
        return (
          <span className="text-sm text-gray-700">
            {datePaid
              ? new Date(datePaid).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : '-'}
          </span>
        );
      },
    },
    {
      key: 'amount',
      label: 'Amount Earned',
      sortable: true,
      hasMenu: true,
      render: (item) => {
        const amount = item.amountEarned 
          ? parseFloat(item.amountEarned) 
          : item.amount;
        return (
          <span className="text-sm text-gray-900 font-medium">
            {formatCurrency(amount)}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className={`${
            item.status === EarningStatus.PAID
              ? 'bg-green-50 text-green-700 border-green-100'
              : 'bg-yellow-50 text-yellow-700 border-yellow-100'
          } px-4 py-1 rounded-lg font-medium capitalize`}
        >
          {item.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <Button
          onClick={() => item.status === EarningStatus.PAID && handleDownloadReceipt(item.id)}
          className={`${
            item.status === EarningStatus.PAID
              ? 'bg-gray-900 hover:bg-gray-800 text-white'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          } px-6 py-2 rounded-xl text-sm font-medium`}
          disabled={item.status === EarningStatus.PENDING}
        >
          Download Receipt
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading earnings...</p>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600">Failed to load earnings data</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
      <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
        {/* Header/Navbar */}
        <PodusCheckerNavigation
          showTitle={false}
          showInfo={false}
          showLocation={false}
          notificationCount={3}
          userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User'}
          userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'U'}
          only={true}
          className="border-b border-gray-100"
        />

        {/* Content */}
        <div className="flex-1 overflow-hidden p-4 md:p-8 lg:p-10 flex flex-col">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Earnings
            </h1>

            {/* Earnings Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-2">
              <div className="bg-gray-50 rounded-2xl p-4 md:p-5">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-0.5">
                  {summary ? formatCurrency(summary.totalEarnings || 0) : '\u20A60'}
                </h2>
                <p className="text-xs text-gray-500">Total Earned</p>
              </div>
              <div className="bg-green-50 rounded-2xl p-4 md:p-5">
                <h2 className="text-2xl md:text-3xl font-bold text-green-700 mb-0.5">
                  {summary ? formatCurrency(summary.paidEarnings || 0) : '\u20A60'}
                </h2>
                <p className="text-xs text-green-600">Paid ({summary?.paidCount || 0})</p>
              </div>
              <div className="bg-amber-50 rounded-2xl p-4 md:p-5">
                <h2 className="text-2xl md:text-3xl font-bold text-amber-700 mb-0.5">
                  {summary ? formatCurrency(summary.pendingEarnings || 0) : '\u20A60'}
                </h2>
                <p className="text-xs text-amber-600">Pending ({summary?.pendingCount || 0})</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4 md:p-5">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-0.5">
                  {summary?.totalCount || 0}
                </h2>
                <p className="text-xs text-gray-500">Investigations</p>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-4 md:mb-6">
            {/* Search */}
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
              />
            </div>

            {/* Filter Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setFilter('All')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'All'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('Pending')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'Pending'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilter('Paid')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'Paid'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Paid
              </button>
            </div>
          </div>

          {/* Table Container */}
          <div className="border border-gray-100 rounded-2xl overflow-hidden flex-1 min-h-0">
            <div className="h-full flex flex-col">
              {paginatedData.length > 0 ? (
                <DataTable
                  data={paginatedData}
                  columns={columns}
                  showCheckbox={true}
                  selectedRows={selectedRows}
                  onRowSelect={handleRowSelect}
                  hoverable={true}
                  striped={true}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">No earnings found</p>
                </div>
              )}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
            {/* Rows per page */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            {/* Page numbers */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ‹
              </button>

              {/* First page */}
              {currentPage > 2 && (
                <>
                  <button 
                    onClick={() => setCurrentPage(1)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    1
                  </button>
                  {currentPage > 3 && <span className="px-2 text-gray-400">...</span>}
                </>
              )}

              {/* Current page and neighbors */}
              {currentPage > 1 && (
                <button 
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  {currentPage - 1}
                </button>
              )}
              
              <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35] text-white">
                {currentPage}
              </button>

              {currentPage < totalPages && (
                <button 
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  {currentPage + 1}
                </button>
              )}

              {/* Last page */}
              {currentPage < totalPages - 1 && (
                <>
                  {currentPage < totalPages - 2 && <span className="px-2 text-gray-400">...</span>}
                  <button 
                    onClick={() => setCurrentPage(totalPages)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
